import React, { useState, useEffect } from 'react';
import { SearchDropdown, DeleteModal } from '../../../../../components';
import { Disco, DiscoType, DiscoDetails } from '../../../../../types';
import { useAppDispatch } from '../../../../../store';
import { updateDiscoData } from '../../../../../store/slices/createCRSlice';

interface DISCOSelectorProps {
  discoNumber: 1 | 2 | 3 | 4 | 5;
  discoData?: {
    rating?: number;
    type?: string;
    model?: string;
    fuses?: number;
    location?: string;
    text?: string;
  };
  isEnabled: boolean;
  canAddNew?: boolean;
  onAddNew?: () => void;
  onCopyFromPrevious?: () => void;
  onDelete?: () => void;
  // Redux data props
  ratings: number[];
  types: DiscoType[];
  availableModels: Disco[];
  discoDetails: DiscoDetails[];
  isLoading: boolean;
  error?: string;
}

export const DISCOSelector: React.FC<DISCOSelectorProps> = ({
  discoNumber,
  discoData,
  isEnabled,
  canAddNew = false,
  onAddNew,
  onCopyFromPrevious,
  onDelete,
  ratings,
  types,
  availableModels,
  discoDetails = [],
  isLoading,
  error
}) => {
  const dispatch = useAppDispatch();
  const [selectedRating, setSelectedRating] = useState<number | undefined>(discoData?.rating);
  const [selectedType, setSelectedType] = useState<DiscoType | undefined>(discoData?.type as DiscoType);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Get text and location options from discoDetails
  const textOptions = discoDetails
    .filter(detail => detail.type === 'text')
    .map(detail => ({
      id: detail.value,
      label: detail.value
    }));

  const locationOptions = discoDetails
    .filter(detail => detail.type === 'location')
    .map(detail => ({
      id: detail.value,
      label: detail.value
    }));

  // Update local state when discoData prop changes
  useEffect(() => {
    setSelectedRating(discoData?.rating);
    setSelectedType(discoData?.type as DiscoType);
  }, [discoData]);

  // Find selected model from available models
  const selectedModel = availableModels?.find(m => m.modelNumber === discoData?.model);

  const handleRatingSelect = (option: any) => {
    const rating = option ? parseInt(option.id) : undefined;
    setSelectedRating(rating);
    dispatch(updateDiscoData({
      disco_number: discoNumber,
      data: { rating, type: undefined, model: undefined, fuses: undefined }
    }));
    setSelectedType(undefined);
  };

  const handleTypeSelect = (option: any) => {
    const type = option?.id;
    setSelectedType(type);
    dispatch(updateDiscoData({
      disco_number: discoNumber,
      data: { type, model: undefined, fuses: undefined }
    }));
  };

  const handleModelSelect = (option: any) => {
    const disco = option ? availableModels?.find(d => d.id === option.id) : undefined;
    dispatch(updateDiscoData({
      disco_number: discoNumber,
      data: { 
        model: disco?.modelNumber,
        rating: disco?.discoRating,
        type: disco?.type
      }
    }));
  };

  const handleFuseRatingSelect = (option: any) => {
    const fuses = option ? parseInt(option.id) : undefined;
    dispatch(updateDiscoData({
      disco_number: discoNumber,
      data: { fuses }
    }));
  };

  const handleLocationSelect = (option: any) => {
    dispatch(updateDiscoData({
      disco_number: discoNumber,
      data: { location: option?.id || undefined }
    }));
  };

  const handleTextSelect = (option: any) => {
    dispatch(updateDiscoData({
      disco_number: discoNumber,
      data: { text: option?.id || undefined }
    }));
  };

  const ratingOptions = ratings?.map(rating => ({
    id: rating.toString(),
    label: `${rating}A`
  })) || [];

  const typeOptions = types?.map(type => ({
    id: type,
    label: type
  })) || [];

  // Filter models based on selected rating and type
  const filteredModels = availableModels?.filter(disco => {
    const matchesRating = !selectedRating || disco.discoRating === selectedRating;
    const matchesType = !selectedType || disco.type === selectedType;
    return matchesRating && matchesType;
  }) || [];

  const modelOptions = filteredModels.map(disco => ({
    id: disco.id,
    label: disco.modelNumber
  })) || [];

  const modelFuseRatings = selectedModel?.fuseRatings ?? [];
  const fuseRatingOptions = modelFuseRatings.map(rating => ({
    id: rating.toString(),
    label: `${rating}A`
  }));


  if (!isEnabled) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-gray-500 text-xs 3xl:text-sm">Select a task to configure DISCO details</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-50/70 backdrop-blur-sm border border-amber-100 rounded-2xl p-4 mb-3 shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-xs 3xl:text-sm font-semibold text-amber-600">
          Disco {discoNumber}
        </h4>
        {onDelete && discoNumber > 1 && (
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-1 text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-2 py-1 rounded text-[10px] 3xl:text-xs font-medium transition-colors duration-200"
            title="Delete DISCO"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Row 1: DISCO Rating, Type & Model */}
        <div className="grid grid-cols-12 gap-3 items-start">
          <div className="col-span-4">
            <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
              DISCO Rating
            </label>
            <SearchDropdown
              options={ratingOptions}
              onSelect={handleRatingSelect}
              placeholder="Choose DISCO rating..."
              isLoading={isLoading}
              error={error}
              className="h-9"
              value={discoData?.rating ? { id: discoData.rating.toString(), label: `${discoData.rating}A` } : null}
              disabled={!isEnabled}
            />
          </div>
          
          <div className="col-span-4">
            <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
              DISCO Type
            </label>
            <SearchDropdown
              options={typeOptions}
              onSelect={handleTypeSelect}
              placeholder="Choose DISCO type..."
              isLoading={isLoading}
              error={error}
              className="h-9"
              disabled={!discoData?.rating || !isEnabled}
              value={discoData?.type ? { id: discoData.type, label: discoData.type } : null}
            />
          </div>

          <div className="col-span-4">
            <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
              DISCO Model
            </label>
            <SearchDropdown
              options={modelOptions}
              onSelect={handleModelSelect}
              placeholder="Choose DISCO model..."
              isLoading={isLoading}
              error={error}
              className="h-9"
              disabled={!discoData?.rating || !discoData?.type || !isEnabled}
              value={selectedModel ? { id: selectedModel.id, label: selectedModel.modelNumber } : null}
            />
          </div>
        </div>

        {/* Row 2: Fuse Rating, Location & Text */}
        <div className="grid grid-cols-12 gap-3 items-start">
          <div className="col-span-4">
            <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
              Fuse Rating {discoData?.type === 'FUSED' ? '' : '(N/A)'}
            </label>
            <SearchDropdown
              options={fuseRatingOptions}
              onSelect={handleFuseRatingSelect}
              placeholder={discoData?.type === 'FUSED' ? "Choose fuse rating..." : "Not applicable"}
              isLoading={isLoading}
              error={error}
              className="h-9"
              disabled={!selectedModel || discoData?.type !== 'FUSED' || fuseRatingOptions.length === 0 || !isEnabled}
              value={discoData?.fuses ? { id: discoData.fuses.toString(), label: `${discoData.fuses}A` } : null}
            />
            {discoData?.type === 'FUSED' && selectedModel && fuseRatingOptions.length === 0 && (
              <p className="mt-1 text-xs 3xl:text-sm text-orange-600 font-medium">No fuse ratings available</p>
            )}
          </div>

          <div className="col-span-4">
            <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
              DISCO Location
            </label>
            <SearchDropdown
              options={locationOptions}
              onSelect={handleLocationSelect}
              placeholder="Choose location..."
              isLoading={isLoading}
              error={error}
              className="h-9"
              disabled={!isEnabled}
              value={discoData?.location ? { id: discoData.location, label: discoData.location } : null}
            />
          </div>

          <div className="col-span-4">
            <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
              DISCO Text
            </label>
            <SearchDropdown
              options={textOptions}
              onSelect={handleTextSelect}
              placeholder="Choose text..."
              isLoading={isLoading}
              error={error}
              className="h-9"
              disabled={!isEnabled}
              value={discoData?.text ? { id: discoData.text, label: discoData.text } : null}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          if (onDelete) {
            onDelete();
          }
          setShowDeleteConfirm(false);
        }}
        title="Delete DISCO"
        message={`Are you sure you want to delete DISCO ${discoNumber}? This action cannot be undone and all configuration data for this DISCO will be permanently removed.`}
        itemType="DISCO"
      />
    </div>
  );
};
